import { getEnv, types, flow, Instance } from "mobx-state-tree";

import { StorageModel } from "../storage/StorageModel";
import { endpoints } from "../api/ApiEndpoints";
import { UserEditInput } from "../../api-models/request/UserInput";
import { UserDetailsResponse } from "../../api-models/response/UserDetailsResponse";
import { currentUserService, noUserData } from "./CurrentUserStorage";
import { permissionsService } from "../authorization/UserPermissionsService";
import { ViewUser } from "../authorization/Permissions";

const UserInViewModel = types.model({
  userInViewId: types.number,
  userInView: types.optional(types.frozen<UserDetailsResponse>(), noUserData),
});

export const UserInViewStorageModel = types
  .compose(UserInViewModel, StorageModel)
  .named("UserInViewStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: UserDetailsResponse) {
        self.userInView = response || noUserData;
      },
      editUserDetails: flow(function* (user: UserEditInput) {
        const endpoint = endpoints.get("user");
        endpoint.routeParams = [self.userInViewId];

        const result = yield getEnv(self).api.put(endpoint, user);

        if (self.userInViewId === currentUserService.currentUser.id)
          yield permissionsService.reloadPermissions();

        return result;
      }),

      removeUser: flow(function* removeUser() {
        const endpoint = endpoints.get("user");
        endpoint.routeParams = [self.userInViewId];

        return yield getEnv(self).api.delete(endpoint);
      }),
    };
  })
  .views((self) => ({
    get canLoadItems() {
      return permissionsService.userHasPermissionTo(ViewUser) && self.userInViewId > 0;
    },
    get userIsLoaded() {
      return !!self.userInView.id;
    },
    get endpoint() {
      const endpoint = endpoints.get("user");
      endpoint.routeParams = [self.userInViewId];
      return endpoint;
    },
    get imageUrl() {
      return self.userInView.image?.url || "";
    },
    get hasImageUploaded() {
      return !!self.userInView.image?.url;
    },
  }))
  .views((self) => ({
    get isLoading() {
      return !self.userIsLoaded;
    },
  }));

export type UserInViewStorage = Instance<typeof UserInViewStorageModel>;
